import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { FormControl, FormHelperText, FormLabel, Stack, TextField, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';

const Name = ({ field, index, option, disabled, setFullName }) => {
	const { getFieldProps, values, setFieldValue, getFieldMeta, getFieldHelpers } =
		useFormikContext();
	const { isProspect } = useClientSchemaContext();
	const { field: fieldConfig } = field;

	const fieldName = `contacts[${index}][${option.name}]`;
	const [name, setName] = useState(values.contacts?.[index]?.[option.name] ?? '');

	useEffect(() => {
		setName(values.contacts?.[index]?.[option.name] ?? '');
	}, [values.contacts?.[index]?.[option.name]]);

	const { error: errorMessage, touched } = getFieldMeta(field.name);
	const error = touched && Boolean(errorMessage);

	const { onBlur: onFieldBlur, ...fieldProps } = getFieldProps(fieldName);

	const { setTouched } = getFieldHelpers(fieldName);

	const onBlur = (e) => {
		onFieldBlur(e);
		setTouched(true, true);
	};

	const required = field.mandatory[isProspect ? 'prospect' : 'client'];

	return (
		<FormControl fullWidth>
			<FormLabel htmlFor={fieldName} required>
				{option.label}
			</FormLabel>
			<TextField
				id={option.name}
				fullWidth
				{...fieldProps}
				value={name}
				onChange={(e) => {
					setName(e.target.value);
					setFullName(option.name, e.target.value);
				}}
				onBlur={async (e) => {
					setFieldValue(fieldName, name.trim());
					setName((prev) => prev.trim());
					setFullName(option.name, name);
					setTimeout(() => {
						onBlur(e);
					}, 100);
				}}
				{...{ error, disabled, required }}
			/>
		</FormControl>
	);
};
const ContactNameField = ({ field, index, disabled }) => {
	const theme = useTheme();
	const { getFieldProps, values, setFieldValue, getFieldMeta, getFieldHelpers, ...formik } =
		useFormikContext();
	const { isProspect } = useClientSchemaContext();
	const { field: fieldConfig } = field;

	const options = useMemo(() => {
		const data = (fieldConfig?.fields ?? []).slice();
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldConfig?.fields]);

	const { error: errorMessage, touched } = getFieldMeta(field.name);
	const error = touched && Boolean(errorMessage);

	const [fullName, setFullName] = useState(values.contacts?.[index]?.[field.fieldName] ?? '');

	useEffect(() => {
		const names = options
			.map((option) => values.contacts?.[index]?.[option.name])
			.filter(Boolean);
		setFieldValue(field.name, names.join(' ').trim());
	}, [options, values.contacts?.[index]]);

	const onFullNameChange = (nameField, newName) => {
		const names = options
			.map((option) =>
				option.name === nameField ? newName : values.contacts?.[index]?.[option.name],
			)
			.filter(Boolean);
		setFullName(names.join(' ').trim());
	};

	return (
		<Stack width='100%'>
			<Stack direction='row' justifyContent={'space-between'} width='100%' spacing={1}>
				{options.map((option, i) => (
					<Name
						key={`contacts-${i}`}
						{...{ field, index, option, disabled, setFullName: onFullNameChange }}
					/>
				))}
			</Stack>
			<Stack
				direction='row'
				justifyContent={error ? 'space-between' : 'flex-end'}
				width='100%'
			>
				{error && <FormHelperText error>{errorMessage}</FormHelperText>}

				{fieldConfig.max && (
					<FormHelperText
						error={error || fullName.length > fieldConfig.max}
						sx={{
							...(fullName.length >= fieldConfig.max && {
								fontWeight: 'bold',
							}),
							mr: '14px',
							fontSize: '0.7rem',
							color: theme.palette.text.secondary,
						}}
					>
						<span>{fullName.length}</span>
						{`/${fieldConfig.max}`}
					</FormHelperText>
				)}
			</Stack>
		</Stack>
	);
};

export default ContactNameField;
